<template>
  <v-container fluid>
    <v-toolbar text color="grey lighten-4" class="mb-3" flat>
      <v-toolbar-title>Payment Terms</v-toolbar-title>
      <v-divider class="mx-2" inset vertical></v-divider>
      <v-radio-group
        v-model="isPercent"
        row
        class="mt-5"
        @change="onIsPercentChange"
      >
        <v-radio label="Percent Based" value="true"></v-radio>
        <v-radio label="Amount Based" value="false"></v-radio>
      </v-radio-group>
      <v-spacer></v-spacer>
      <v-dialog v-model="dialog" max-width="600px" persistent>
        <template v-slot:activator="{ on }" v-if="allowAgentEdit">
          <v-btn text v-on="on" color="primary">
            <v-icon left>mdi-plus-circle</v-icon>Add Payment Term
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="grey lighten-3">
            <v-row>
              <v-icon class="draggable">mdi-drag</v-icon>
              <v-col class="py-0">{{ formTitle }}</v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pb-0">
            <v-container grid-list-md class="px-0">
              <v-layout wrap>
                <v-flex xs12 sm6>
                  <v-select
                    v-model="editedItem.termType"
                    :items="termTypes"
                    label="Type"
                    :outlined="outlinedInputs"
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-text-field
                    v-model="editedItem.days"
                    label="Days"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-text-field
                    v-model="editedItem.amount"
                    :label="editLabel"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions class="grey lighten-4">
            <v-spacer></v-spacer>
            <v-btn color="warning" text @click="close">Cancel</v-btn>
            <v-btn color="primary" @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <!-- Terms carry a figure per mode, so switching the view shows a blank where nothing
         has been entered for it yet -- an amount cannot be turned into a percentage
         without a price. Say so, or it reads as data loss. #11412 -->
    <v-alert
      v-if="missingValueCount"
      type="info"
      dense
      outlined
      class="mb-3"
    >
      {{ missingValueCount }}
      {{ missingValueCount > 1 ? "terms have" : "term has" }} no
      {{ isPercent == "true" ? "percentage" : "amount" }} entered yet. The
      {{ isPercent == "true" ? "amounts" : "percentages" }} already entered are
      kept and come back if you switch the view.
    </v-alert>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="terms"
        item-key="uid"
        v-bind="pagingOptions"
        ><template v-slot:item.amount="{ item }">
          {{
            isPercent == "true"
              ? item.amount
                ? `${item.amount}%`
                : ""
              : item.amount
              ? item.amount
              : ""
          }}
        </template>
        <template v-slot:item.actions="{ item }" v-if="allowAgentEdit">
          <default-list-actions
            :item="item"
            :hide-view="true"
            @deleteItem="deleteItem($event)"
            @editItem="editItem($event)"
          ></default-list-actions>
        </template>
      </v-data-table>
    </v-card>
    <div v-if="debug">
      <pre>{{ terms }}</pre>
      <pre>{{ editedItem }}</pre>
    </div>
  </v-container>
</template>

<script>
import randomize from "randomatic";
import DefaultListActions from "../DefaultListActions";
import { pagingOptions } from "../../../../helpers/index";

export default {
  name: "PaymentTerms",
  props: {
    //implements v-model
    value: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    DefaultListActions,
  },
  data() {
    return {
      pagingOptions,
      debug: false,
      dialog: false,
      isPercent:
        this.value && this.value[0]
          ? this.value[0].isPercent.toString()
          : "false",
      headers: [
        {
          text: "Days",
          align: "left",
          value: "days",
        },
        {
          text:
            this.value &&
            this.value[0] &&
            this.value[0].isPercent.toString() == "true"
              ? "Percent"
              : "Amount",
          align: "left",
          value: "amount",
        },
        {
          text: "Term Type",
          align: "left",
          value: "termType",
          name: "termType",
        },
        {
          text: "Actions",
          sortable: false,
          align: "center",
          value: "actions",
        },
      ],
      terms: this.value.length ? this.value.map(this.termsListInputMapper) : [],
      editedItem: {
        days: null,
        amount: null,
        altAmount: null,
        termType: "Deposit",
      },
      defaultItem: {
        days: null,
        amount: null,
        altAmount: null,
        termType: "Deposit",
      },
      termTypes: ["Deposit", "Part Payment", "Balance"],
    };
  },
  watch: {
    value(newList) {
      if (newList && newList.length) {
        this.terms = newList.map(this.termsListInputMapper);
        this.isPercent = newList[0].isPercent.toString();
        this.headers[1].text = this.isPercent == "true" ? "Percent" : "Amount";
      } else {
        this.terms = [];
      }
    },
  },
  computed: {
    allowAgentEdit() {
      return (
        !this.$store.getters.hasAccessTo("agents:view_only") &&
        this.$store.getters.hasAccessTo("agents:edit_agent")
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    formTitle() {
      return "Edit Payment Term";
    },
    editLabel() {
      return this.isPercent == "true" ? "Percent" : "Amount";
    },
    // Terms that need a figure for the mode now selected and do not have one. Deposit and
    // Part Payment only -- a Balance with no value is the implicit remainder, which is the
    // normal way to enter it (see validate()). #11412
    missingValueCount() {
      return this.terms.filter(
        (term) =>
          ["Deposit", "Part Payment"].includes(term.termType) &&
          (term.amount === null ||
            term.amount === "" ||
            term.amount === undefined)
      ).length;
    },
  },
  methods: {
    termsListInputMapper(item) {
      const { __typename, ...term } = item;
      term.uid = randomize("Aa0", 32);
      // altAmount is absent on every term saved before #11412. Declare it up front so
      // Vue tracks it when the mode toggle swaps it with amount.
      if (term.altAmount === undefined) {
        term.altAmount = null;
      }
      return term;
    },
    termsListOutputMapper(item) {
      return {
        isPercent: this.isPercent == "true",
        days:
          item.days !== null && item.days !== "" && item.days !== undefined
            ? Number(item.days)
            : null,
        amount:
          item.amount !== null &&
          item.amount !== "" &&
          item.amount !== undefined
            ? Number(item.amount)
            : null,
        // The value for the mode that is NOT selected, carried so switching back and
        // forth does not lose it. #11412
        altAmount:
          item.altAmount !== null &&
          item.altAmount !== "" &&
          item.altAmount !== undefined
            ? Number(item.altAmount)
            : null,
        termType: item.termType,
      };
    },
    editItem(item) {
      //this.editedIndex = this.terms.indexOf(item); // https://tracker.globetrack.ie/issues/6227 cant find index of object this way...
      this.editedIndex = this.terms
        .map((t) => `${t.amount}_${t.days}_${t.uid}`)
        .indexOf(`${item.amount}_${item.days}_${item.uid}`);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      const index = this.terms.indexOf(item);
      this.terms.splice(index, 1);
      this.dispatchChange();
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.dispatchChange();
      }, 300);
    },

    validate() {
      const termType = this.editedItem.termType.toString();
      const days = this.editedItem.days;
      const amount = this.editedItem.amount;

      // Balance and Part Payment require Days to be filled (Deposit does not)
      if (["Balance", "Part Payment"].includes(termType)) {
        if (days === null || days === "" || days === undefined) {
          alert("Days is required for Balance and Part Payment");
          return false;
        }
      }

      // Deposit and Part Payment require Amount/Percent to be filled (Balance does not)
      if (["Deposit", "Part Payment"].includes(termType)) {
        if (amount === null || amount === "" || amount === undefined) {
          alert("Amount/Percent is required for Deposit and Part Payment");
          return false;
        }
      }

      return true;
    },

    save() {
      if (!this.validate()) {
        return;
      }
      if (this.editedIndex > -1) {
        Object.assign(this.terms[this.editedIndex], this.editedItem);
      } else {
        this.terms.push(this.editedItem);
      }
      this.close();
      this.dispatchChange();
    },
    onIsPercentChange($event) {
      // A term carries a value for both modes: `amount` is always the value for the mode
      // currently selected, `altAmount` the other one. Swap them so each view shows the
      // figures entered for THAT view. Before #11412 the toggle only re-labelled the
      // column, so an amount of 500.00 read as "500%" after switching -- and saving in
      // that state stored 500 as a percentage.
      this.terms.forEach((term) => {
        const previous = term.amount;
        term.amount = term.altAmount === undefined ? null : term.altAmount;
        term.altAmount = previous === undefined ? null : previous;
      });
      this.headers[1].text = this.isPercent == "true" ? "Percent" : "Amount";
      this.dispatchChange();
    },
    dispatchChange() {
      this.$emit("input", this.terms.map(this.termsListOutputMapper));
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/shared/payment-terms/PaymentTerms.vue